import { useState } from 'react';
import { newsData } from '../../mocks/news';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function BlogPage() {
  const [selectedCategory] = useState('All');

  const filteredNews = newsData;

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#F5E6D3] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="w-16 h-1 bg-[#D4A574] mb-4"></div>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#2C1810] mb-6 leading-tight font-['Space_Mono']">
              OUR BLOG
            </h1>
            <p className="text-xl text-[#5C4033] leading-relaxed font-['DM_Sans']">
              Insights, wisdom, and stories about building a lasting legacy through faith and stewardship
            </p>
          </div>
        </div>
      </section>

      {/* Empty State or Content */}
      {filteredNews.length === 0 ? (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-[#F5E6D3] border-4 border-[#2C1810] p-16 text-center">
              <div className="max-w-2xl mx-auto">
                <div className="w-20 h-20 bg-[#D4A574] mx-auto mb-6 flex items-center justify-center">
                  <i className="ri-article-line text-4xl text-[#2C1810]"></i>
                </div>
                <h2 className="text-3xl font-bold text-[#2C1810] mb-4 font-['Space_Mono']">
                  NO POSTS YET
                </h2>
                <p className="text-lg text-[#5C4033] font-['DM_Sans'] mb-8">
                  We're working on creating meaningful content for you. Check back soon for insights, wisdom, and stories about building a lasting legacy.
                </p>
                <a 
                  href="/admin"
                  className="inline-block px-8 py-3 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                >
                  GO TO ADMIN
                </a>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Featured Post */}
          <section className="py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <p className="text-sm font-semibold tracking-widest text-[#D4A574] mb-6 font-['Space_Mono']">
                FEATURED POST
              </p>
              <div className="group relative border-4 border-[#2C1810] overflow-hidden cursor-pointer hover:border-[#D4A574] transition-all">
                <div className="relative h-[500px] lg:h-[600px]">
                  <img 
                    src={filteredNews[0].image} 
                    alt={filteredNews[0].title}
                    className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/95 via-[#2C1810]/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
                    <div className="max-w-4xl">
                      <span className="inline-block px-5 py-2 bg-[#D4A574] text-[#2C1810] text-sm font-semibold font-['Space_Mono'] mb-5">
                        {filteredNews[0].category.toUpperCase()}
                      </span>
                      <h2 className="text-4xl lg:text-5xl font-bold text-[#F5E6D3] mb-4 leading-tight font-['Space_Mono']">
                        {filteredNews[0].title}
                      </h2>
                      <p className="text-[#F5E6D3]/90 text-lg mb-6 font-['DM_Sans']">{filteredNews[0].excerpt}</p>
                      <div className="flex items-center gap-4 text-[#D4A574] text-sm font-['Space_Mono']">
                        <span>{filteredNews[0].date}</span>
                        <span>•</span>
                        <span>{filteredNews[0].readTime}</span>
                        {filteredNews[0].videoUrl && (
                          <>
                            <span>•</span>
                            <span>📹 VIDEO</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Video Section */}
                {filteredNews[0].videoUrl && (
                  <div className="bg-[#2C1810] p-8">
                    <div className="aspect-video w-full">
                      <iframe
                        src={filteredNews[0].videoUrl}
                        className="w-full h-full border-2 border-[#D4A574]"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={filteredNews[0].title}
                      ></iframe>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Blog Grid */}
          {filteredNews.length > 1 && (
            <section className="py-16 lg:py-24 bg-[#F5E6D3]">
              <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <h3 className="text-3xl font-bold text-[#2C1810] mb-12 font-['Space_Mono']">
                  RECENT POSTS
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredNews.slice(1).map((post, index) => (
                    <article 
                      key={index}
                      className="group bg-white border-4 border-[#2C1810] overflow-hidden hover:border-[#D4A574] transition-all cursor-pointer"
                    >
                      <div className="relative h-64 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        {post.videoUrl && (
                          <div className="absolute top-4 right-4 w-12 h-12 bg-[#D4A574] flex items-center justify-center">
                            <i className="ri-play-fill text-2xl text-[#2C1810]"></i>
                          </div>
                        )}
                      </div>
                      <div className="p-6 bg-[#F5E6D3]">
                        <span className="inline-block px-3 py-1.5 border-2 border-[#2C1810] text-[#2C1810] text-xs font-semibold font-['Space_Mono'] mb-4">
                          {post.category.toUpperCase()}
                        </span>
                        <h3 className="text-xl font-bold text-[#2C1810] mb-3 line-clamp-2 leading-tight font-['Space_Mono'] group-hover:text-[#D4A574] transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-[#5C4033] text-sm mb-4 line-clamp-3 font-['DM_Sans']">{post.excerpt}</p>
                        <div className="flex items-center justify-between pt-4 border-t-2 border-[#2C1810]">
                          <span className="text-[#D4A574] text-xs font-['Space_Mono']">{post.date}</span>
                          <span className="text-[#D4A574] text-xs font-['Space_Mono']">{post.readTime}</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </div>
  );
}
